/**
 * Prázdný stav pro metriky odvozené z tržeb.
 *
 * Ve fixture / dev prostředí je `price` = 0. Vykreslit „0 Kč" nebo „eCPM 0,00"
 * by bylo tvrzení o datech, které není pravda — naměřená nula a nedostupné
 * měření jsou dvě různé věci. Tahle komponenta to říká nahlas.
 *
 * Používej všude, kde by jinak byla nula: KPI karty, sloupce tabulky,
 * ekonomická záložka, finanční panel debtu.
 */
import { revenueUnavailableReason } from '@/lib/revenue';
import { EmptyState } from './EmptyState';

export interface NoRevenueDataProps {
  /** `fixture` = prostředí tržby nemá; `no_data` = období je prázdné */
  context?: 'fixture' | 'no_data';
  /** čeho se to týká — do nadpisu */
  what?: string;
  compact?: boolean;
}

export function NoRevenueData({
  context = 'fixture',
  what = 'Tržby a odvozené metriky',
  compact = false,
}: NoRevenueDataProps) {
  return (
    <EmptyState
      compact={compact}
      title={`${what} nejsou k dispozici`}
      description={<p>{revenueUnavailableReason(context)}</p>}
      icon={
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" />
          <path d="M8 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      }
    />
  );
}

/**
 * Inline varianta do buňky tabulky nebo KPI karty — pomlčka s vysvětlením
 * v tooltipu. Nikdy nevrací „0".
 */
export function RevenueUnavailable({ context = 'fixture' }: { context?: 'fixture' | 'no_data' }) {
  return (
    <span className="revenue-unavailable" title={revenueUnavailableReason(context)}>
      —<span className="sr-only"> data o tržbách nejsou k dispozici</span>
    </span>
  );
}

/**
 * Banner nad stránkou, když celé prostředí nemá tržby. Ať to uživatel vidí
 * jednou nahoře a nediví se u každého sloupce zvlášť.
 */
export function NoRevenueBanner({ context = 'fixture' }: { context?: 'fixture' | 'no_data' }) {
  return (
    <div className="banner banner--info" role="status">
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M8 7v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="8" cy="4.8" r="0.75" fill="currentColor" />
      </svg>
      <span>
        <strong>Bez dat o tržbách.</strong> {revenueUnavailableReason(context)} Sloupce
        tržby, eCPM, fill rate a win rate proto zůstávají prázdné — nezobrazujeme nuly,
        které by vypadaly jako naměřený výsledek.
      </span>
    </div>
  );
}
